<template>
  <BaseDialogForm :is-edit="isEdit">
    <template v-slot:toggle="{ on }">
      <v-btn v-if="!isEdit" color="primary" v-on="on" @click.stop="onOpen()">
        <v-icon left>
          add_box
        </v-icon>
        New Raw Material
      </v-btn>
      <v-btn v-else v-on="on" @click.stop="onOpen()" icon>
        <v-icon>
          edit
        </v-icon>
      </v-btn>
    </template>
    <template v-slot:title>
      <h2 v-if="isEdit">Edit</h2>
      <h2 v-else>Make A New Raw Material</h2>
    </template>
    <template v-slot:content>
      <v-form v-model="$v.valid" @submit.prevent="submit">
        <v-text-field
          v-model.trim="$v.formdata.name.$model"
          :error-messages="nameErrors"
          label="Raw Material Name"
          counter="48"
          hint="Just something unique to help identify the material"
          :prepend-icon="formdata.icon"
          @input="$v.formdata.name.$touch()"
          @blur="$v.formdata.name.$touch()"
        />
        <v-text-field
          v-model="$v.formdata.lotnumber.$model"
          :error-messages="lotnumberErrors"
          label="Lot Number"
          counter="48"
          hint="Typically found on the raw material label"
          :prepend-icon="formdata.lotnumberIcon"
          @input="$v.formdata.lotnumber.$touch()"
          @blur="$v.formdata.lotnumber.$touch()"
        />
        <v-text-field
          v-model="$v.formdata.original.$model"
          :error-messages="originalErrors"
          label="Original Quantity"
          counter="13"
          :suffix="formdata.units"
          hint="Weight on arrival"
          type="Number"
          prepend-icon="fas fa-weight"
          @input="$v.formdata.original.$touch()"
          @blur="$v.formdata.original.$touch()"
        />
        <v-text-field
          v-model="$v.formdata.quantity.$model"
          :error-messages="quantityErrors"
          label="Current Quantity"
          counter="13"
          :suffix="formdata.units"
          hint="Actual Weight"
          type="Number"
          prepend-icon="fas fa-weight"
          @input="$v.formdata.quantity.$touch()"
          @blur="$v.formdata.quantity.$touch()"
        />
        <v-select
          v-model="$v.formdata.units.$model"
          label="Units"
          :items="units"
          :error-messages="unitErrors"
          prepend-icon="fas fa-dumbbell"
          @input="$v.formdata.units.$touch()"
          @blur="$v.formdata.units.$touch()"
        />
        <v-autocomplete
          v-model="$v.formdata.location_id.$model"
          :error-messages="locationErrors"
          label="Storage Location"
          :items="locations"
          :loading="loading"
          item-value="id"
          item-text="name"
          hint="Where are we storing this thing? You can add a new location in the field below if needed."
          prepend-icon="fas fa-map-marked-alt"
          @input="$v.formdata.location_id.$touch()"
          @blur="$v.formdata.location_id.$touch()"
        />
        <v-textarea
          v-model="formdata.notes"
          label="Extra Notes"
          prepend-icon="note"
        >
        </v-textarea>
      </v-form>
    </template>

    <template #actions="{ close }">
      <v-btn color="warning" dark @click="clear">
        <v-icon left>fas fa-redo</v-icon>
        Clear
      </v-btn>
      <v-btn
        :disabled="$v.$invalid"
        color="primary"
        @click="
          submit();
          close();
        "
      >
        <v-icon dark left>
          fas fa-save
        </v-icon>
        Save
      </v-btn>
    </template>
  </BaseDialogForm>
</template>

<script>
import { validationMixin } from "vuelidate";
import { handleErrors } from "../../services/formErrors";
import RawMaterial from "../../models/RawMaterial";
import Location from "../../models/Location";
import BaseDialogForm from "../Base/BaseDialogForm";
export default {
  name: "RawMaterialForm",
  components: { BaseDialogForm },
  mixins: [validationMixin],
  validations: RawMaterial.validations,
  props: {
    instance: {
      type: Object,
      default: () => {}
    },
    isEdit: {
      type: Boolean,
      required: true,
      default: false
    },
    materialid: {
      required: true
    }
  },
  data() {
    return {
      loading: true,
      formdata: new RawMaterial(this.instance),
      units: ["g", "kg", "mg", 'mL', 'L', "units"],
    };
  },
  computed: {
    nameErrors() {
      return handleErrors("formdata", "name", this);
    },
    lotnumberErrors() {
      return handleErrors("formdata", "lotnumber", this);
    },
    originalErrors() {
      return handleErrors("formdata", "original", this);
    },
    quantityErrors() {
      return handleErrors("formdata", "quantity", this);
    },
    unitErrors() {
      return handleErrors("formdata", "units", this);
    },
    locationErrors() {
      return handleErrors("formdata", "location_id", this);
    },
    locations() {
      return Location.query().orderBy('name').get();
    }
  },
  methods: {
    onOpen() {
      this.initialize();
    },
    initialize() {
      Location.$fetch().then(() => this.loading = false);
    },
    submit() {
      this.$v.$touch();
      if (this.isEdit) {
        RawMaterial
          .$put(this.instance.id, this.formdata.toUpdatePayload())
      } else {
        this.formdata.material_id = this.materialid;
        RawMaterial
          .$post(this.formdata.toCreatePayload())
          .then(() => this.clear())
      }
    },
    clear() {
      this.formdata = new RawMaterial();
      this.$v.$reset();
    }
  }
};
</script>
